import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min';
import './rvStyle.css';
import config from '../../config.json';

const apiUrl = config.apiUrl;

const Statuses = {
    Pending: 0,
    Approved: 1,
    Rejected: 2
};

const RVTransactions = () => {
    const [overtimeData, setOvertimeData] = useState([]);
    const [leavetimeData, setLeaveData] = useState([]);
    const [undertimeData, setUndertimeData] = useState([]);
    const [timeEntryData, setTimeEntryData] = useState([]);

    const [overtimeCurrentPage, setOvertimeCurrentPage] = useState(1);
    const [overtimeTotalPages, setOvertimeTotalPages] = useState(0);
    const [overtimeItemsPerPage] = useState(5);

    const [leaveCurrentPage, setLeaveCurrentPage] = useState(1);
    const [leaveTotalPages, setLeaveTotalPages] = useState(0);
    const [leaveItemsPerPage] = useState(5);

    const [undertimeCurrentPage, setUndertimeCurrentPage] = useState(1);
    const [undertimeTotalPages, setUndertimeTotalPages] = useState(0);
    const [undertimeItemsPerPage] = useState(5);

    const [timeEntryCurrentPage, setTimeEntryCurrentPage] = useState(1);
    const [timeEntryTotalPages, setTimeEntryTotalPages] = useState(0);
    const [timeEntryItemsPerPage] = useState(5);

    const [isOvertimeLoading, setIsOvertimeLoading] = useState(false);
    const [isLeaveLoading, setIsLeaveLoading] = useState(false);
    const [isUndertimeLoading, setIsUndertimeLoading] = useState(false);
    const [isTimeEntryLoading, setIsTimeEntryLoading] = useState(false);

    const [employeeList, setEmployeeList] = useState([]);
    const [dateFrom, setDateFrom] = useState(new Date().toISOString().split('T')[0]);
    const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState('0');
    const [showOvertime, setShowOvertime] = useState(true);
    const [showLeave, setShowLeave] = useState(true);
    const [showUndertime, setShowUndertime] = useState(true);
    const [showTimeEntry, setShowTimeEntry] = useState(true);
    const navigate = useNavigate();
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [statusFilters, setStatusFilters] = useState({
        Pending: false,
        Approved: true,
        Rejected: false,
    });

    const roles = JSON.parse(localStorage.getItem('Roles'));

    const userAccess = {
        hasView: roles?.includes('View') || false,
        hasAdmin: roles?.includes('Admin') || false,
        hasApprover: roles?.includes('Approver') || false,
        hasStandard: roles?.includes('Standard') || false,
        hasReviewer: roles?.includes('Reviewer') || false,
    };

    const { hasAdmin, hasReviewer } = userAccess;

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token === null) {
            window.location.reload();
        }

        fetchEmployeeData();
    }, []);

    const fetchOvertimeData = useCallback(async (pageNumber) => {
        setIsOvertimeLoading(true);
        const token = localStorage.getItem('token');
        const statusQueryString = Object.keys(statusFilters)
            .filter((key) => statusFilters[key])
            .map((key) => `statuses=${Statuses[key]}`)
            .join('&');

        try {
            const response = await fetch(
                `${apiUrl}/ReportView/Overtime?DateFrom=${dateFrom}&DateTo=${dateTo}&${statusQueryString}&EmployeeId=${selectedEmployeeId}&pageNumber=${pageNumber}&pageSize=${overtimeItemsPerPage}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.ok) {
                const result = await response.json();
                setOvertimeData(result.Data || []);
                setOvertimeTotalPages(result.TotalCount ? Math.ceil(result.TotalCount / overtimeItemsPerPage) : 0);
                setOvertimeCurrentPage(result.CurrentPage || pageNumber);
            }
        } catch (error) {
            setErrorMessage('Error fetching overtime data');
            setSuccessMessage('');
        } finally {
            setIsOvertimeLoading(false);
        }
    }, [dateFrom, dateTo, statusFilters, selectedEmployeeId, overtimeItemsPerPage]);

    const fetchLeaveData = useCallback(async (pageNumber) => {
        setIsLeaveLoading(true);
        const token = localStorage.getItem('token');
        const statusQueryString = Object.keys(statusFilters)
            .filter((key) => statusFilters[key])
            .map((key) => `statuses=${Statuses[key]}`)
            .join('&');

        try {
            const response = await fetch(
                `${apiUrl}/ReportView/Leave?DateFrom=${dateFrom}&DateTo=${dateTo}&${statusQueryString}&EmployeeId=${selectedEmployeeId}&pageNumber=${pageNumber}&pageSize=${leaveItemsPerPage}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.ok) {
                const result = await response.json();
                setLeaveData(result.Data || []);
                setLeaveTotalPages(result.TotalCount ? Math.ceil(result.TotalCount / leaveItemsPerPage) : 0);
                setLeaveCurrentPage(result.CurrentPage || pageNumber);
            }
        } catch (error) {
            setErrorMessage('Error fetching leave data');
            setSuccessMessage('');
        } finally {
            setIsLeaveLoading(false);
        }
    }, [dateFrom, dateTo, statusFilters, selectedEmployeeId, leaveItemsPerPage]);

    const fetchUndertimeData = useCallback(async (pageNumber) => {
        setIsUndertimeLoading(true);
        const token = localStorage.getItem('token');
        const statusQueryString = Object.keys(statusFilters)
            .filter((key) => statusFilters[key])
            .map((key) => `statuses=${Statuses[key]}`)
            .join('&');

        try {
            const response = await fetch(
                `${apiUrl}/ReportView/Undertime?DateFrom=${dateFrom}&DateTo=${dateTo}&${statusQueryString}&EmployeeId=${selectedEmployeeId}&pageNumber=${pageNumber}&pageSize=${undertimeItemsPerPage}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.ok) {
                const result = await response.json();
                setUndertimeData(result.Data || []);
                setUndertimeTotalPages(result.TotalCount ? Math.ceil(result.TotalCount / undertimeItemsPerPage) : 0);
                setUndertimeCurrentPage(result.CurrentPage || pageNumber);
            }
        } catch (error) {
            setErrorMessage('Error fetching Undertime data');
            setSuccessMessage('');
        } finally {
            setIsUndertimeLoading(false);
        }
    }, [dateFrom, dateTo, statusFilters, selectedEmployeeId, undertimeItemsPerPage]);

    const fetchTimeEntryData = useCallback(async (pageNumber) => {
        setIsTimeEntryLoading(true);
        const token = localStorage.getItem('token');
        const statusQueryString = Object.keys(statusFilters)
            .filter((key) => statusFilters[key])
            .map((key) => `statuses=${Statuses[key]}`)
            .join('&');

        try {
            const response = await fetch(
                `${apiUrl}/ReportView/TimeEntry?DateFrom=${dateFrom}&DateTo=${dateTo}&${statusQueryString}&EmployeeId=${selectedEmployeeId}&pageNumber=${pageNumber}&pageSize=${timeEntryItemsPerPage}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.ok) {
                const result = await response.json();
                setTimeEntryData(result.Data || []);
                setTimeEntryTotalPages(result.TotalCount ? Math.ceil(result.TotalCount / timeEntryItemsPerPage) : 0);
                setTimeEntryCurrentPage(result.CurrentPage || pageNumber);
            }
        } catch (error) {
            setErrorMessage('Error fetching Time Entry data');
            setSuccessMessage('');
        } finally {
            setIsTimeEntryLoading(false);
        }
    }, [dateFrom, dateTo, statusFilters, selectedEmployeeId, timeEntryItemsPerPage]);

    const fetchEmployeeData = async () => {
        setIsLoading(true);
        const token = localStorage.getItem('token');

        try {
            const response = await fetch(`${apiUrl}/Employee?pageNumber=1&pageSize=1000`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await response.json();
            if (!response.ok) {
                if (response.status === 401) {
                    window.location.href = `${config.identityUrl}/Identity/Account/Login`;
                } else {
                    setErrorMessage('Failed to fetch users data');
                    setSuccessMessage('');
                }

                return;
            }

            const employeeListData = Array.isArray(data) ? data : data.Data || [];
            const sortedEmployeeList = employeeListData
                .slice()
                .sort((a, b) => {
                    const aName = `${a.FirstName ?? ''} ${a.MiddleInitial ?? ''} ${a.LastName ?? ''}`.trim();
                    const bName = `${b.FirstName ?? ''} ${b.MiddleInitial ?? ''} ${b.LastName ?? ''}`.trim();
                    return aName.localeCompare(bName);
                });
            setEmployeeList(sortedEmployeeList);
        } catch (error) {
            setErrorMessage('Error fetching employees data');
            setSuccessMessage('');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEmployeeChange = (event) => {
        setSelectedEmployeeId(event.target.value);
    };

    const handleCheckboxChange = (event) => {
        const { name, checked } = event.target;

        if (name === 'Overtime') {
            setShowOvertime(checked);
        } else if (name === 'Leave') {
            setShowLeave(checked);
        } else if (name === 'Undertime') {
            setShowUndertime(checked);
        } else if (name === 'Time Entry') {
            setShowTimeEntry(checked);
        } else {
            setStatusFilters({
                ...statusFilters,
                [name]: checked,
            });
        }
    };

    const openAccordionSection = (sectionId) => {
        const section = document.getElementById(sectionId);
        if (section) {
            section.classList.add('show');
            section.classList.remove('collapse');
        }

        const button = document.querySelector(`[data-bs-target="#${sectionId}"]`);
        if (button) {
            button.classList.remove('collapsed');
        }
    };

    useEffect(() => {
        fetchOvertimeData(overtimeCurrentPage);
    }, [fetchOvertimeData, overtimeCurrentPage]);

    useEffect(() => {
        fetchLeaveData(leaveCurrentPage);
    }, [fetchLeaveData, leaveCurrentPage]);

    useEffect(() => {
        fetchUndertimeData(undertimeCurrentPage);
    }, [fetchUndertimeData, undertimeCurrentPage]);

    useEffect(() => {
        fetchTimeEntryData(timeEntryCurrentPage);
    }, [fetchTimeEntryData, timeEntryCurrentPage]);

    const handleFilterButtonClick = async () => {
        setErrorMessage('');
        setSuccessMessage('');

        if (showOvertime) {
            if (overtimeCurrentPage === 1) {
                await fetchOvertimeData(1);
            } else {
                setOvertimeCurrentPage(1);
            }
            openAccordionSection('collapseOvertime');
        }

        if (showLeave) {
            if (leaveCurrentPage === 1) {
                await fetchLeaveData(1);
            } else {
                setLeaveCurrentPage(1);
            }
            openAccordionSection('collapseLeave');
        }

        if (showUndertime) {
            if (undertimeCurrentPage === 1) {
                await fetchUndertimeData(1);
            } else {
                setUndertimeCurrentPage(1);
            }
            openAccordionSection('collapseUndertime');
        }

        if (showTimeEntry) {
            if (timeEntryCurrentPage === 1) {
                await fetchTimeEntryData(1);
            } else {
                setTimeEntryCurrentPage(1);
            }
            openAccordionSection('collapseTimeEntry');
        }
    };

    const renderPagination = (currentPage, totalPages, setCurrentPage, ariaLabel) => {
        const safeTotalPages = totalPages || 1;
        const pageRange = 5;
        const startPage = Math.max(1, currentPage - Math.floor(pageRange / 2));
        const endPage = Math.min(safeTotalPages, startPage + pageRange - 1);

        return (
            <nav aria-label={ariaLabel}>
                <ul className="pagination mt-3">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(1)} aria-label="First">
                            <span aria-hidden="true">{"<<"}</span>
                        </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} aria-label="Previous">
                            <span aria-hidden="true">{"<"}</span>
                        </button>
                    </li>

                    {Array.from({ length: endPage - startPage + 1 }, (_, index) => {
                        const page = startPage + index;
                        return (
                            <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                <button className="page-link" onClick={() => setCurrentPage(page)}>
                                    {page}
                                </button>
                            </li>
                        );
                    })}

                    <li className={`page-item ${currentPage === safeTotalPages || safeTotalPages === 0 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} aria-label="Next">
                            <span aria-hidden="true">{">"}</span>
                        </button>
                    </li>
                    <li className={`page-item ${currentPage === safeTotalPages || safeTotalPages === 0 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(safeTotalPages)} aria-label="Last">
                            <span aria-hidden="true">{">>"}</span>
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    return (
        <div>
            {(hasAdmin || hasReviewer) ? (
                <>
                    {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                    {successMessage && <div className="alert alert-success">{successMessage}</div>}
                    <div className="accordion" id="accordionFilter">
                        <div className="accordion-item">
                            <h2 className="accordion-header" id="headingFilter">
                                <button
                                    className="accordion-button"
                                    type="button"
                                    data-bs-toggle="collapse"
                                    data-bs-target="#collapseFilter"
                                    aria-expanded="true"
                                    aria-controls="collapseFilter"
                                >
                                    Filter Options
                                </button>
                            </h2>
                            <div
                                id="collapseFilter"
                                className="accordion-collapse collapse show"
                                aria-labelledby="headingFilter"
                                data-bs-parent="#accordionFilter"
                            >
                                <div className="accordion-body">
                                    <div className="row mt-3">
                                        <div className="col-md-6">
                                            <label className="form-label">Date From:</label>
                                            <input type="date" className="form-control" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Date To:</label>
                                            <input type="date" className="form-control" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                                        </div>

                                        <div className="col-md-6 mt-2">
                                            <div className="form-check">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="overtimeCheckbox"
                                                    name="Overtime"
                                                    checked={showOvertime}
                                                    onChange={handleCheckboxChange}
                                                />
                                                <label className="form-check-label" htmlFor="overtimeCheckbox">
                                                    Overtime
                                                </label>
                                            </div>
                                            <div className="form-check">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="leaveCheckbox"
                                                    name="Leave"
                                                    checked={showLeave}
                                                    onChange={handleCheckboxChange}
                                                />
                                                <label className="form-check-label" htmlFor="leaveCheckbox">
                                                    Leave
                                                </label>
                                            </div>
                                            <div className="form-check">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="undertimeCheckbox"
                                                    name="Undertime"
                                                    checked={showUndertime}
                                                    onChange={handleCheckboxChange}
                                                />
                                                <label className="form-check-label" htmlFor="undertimeCheckbox">
                                                    Undertime
                                                </label>
                                            </div>
                                            <div className="form-check">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="timeentryCheckbox"
                                                    name="Time Entry"
                                                    checked={showTimeEntry}
                                                    onChange={handleCheckboxChange}
                                                />
                                                <label className="form-check-label" htmlFor="timeentryCheckbox">
                                                    Time Entry
                                                </label>
                                            </div>
                                        </div>

                                        <div className="col-md-6 mt-2">
                                            <div className="form-check">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="pendingCheckbox"
                                                    name="Pending"
                                                    checked={statusFilters.Pending}
                                                    onChange={handleCheckboxChange}
                                                />
                                                <label className="form-check-label" htmlFor="pendingCheckbox">
                                                    Pending
                                                </label>
                                            </div>
                                            <div className="form-check">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="approvedCheckbox"
                                                    name="Approved"
                                                    checked={statusFilters.Approved}
                                                    onChange={handleCheckboxChange}
                                                />
                                                <label className="form-check-label" htmlFor="approvedCheckbox">
                                                    Approved
                                                </label>
                                            </div>
                                            <div className="form-check">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="rejectedCheckbox"
                                                    name="Rejected"
                                                    checked={statusFilters.Rejected}
                                                    onChange={handleCheckboxChange}
                                                />
                                                <label className="form-check-label" htmlFor="rejectedCheckbox">
                                                    Rejected
                                                </label>
                                            </div>
                                        </div>

                                        <div className="col-md-6 mt-2">
                                            <label htmlFor="EmployeeId" className="form-label">Employee</label>
                                            <select
                                                className="form-select"
                                                type="text"
                                                id="EmployeeId"
                                                name="EmployeeId"
                                                onChange={handleEmployeeChange}
                                                value={selectedEmployeeId}
                                            >
                                                {isLoading ? (
                                                    <option value="">Loading...</option>
                                                ) : (
                                                    <>
                                                        <option value="0">All</option>
                                                        {employeeList.map((employee) => (
                                                            <option key={employee.ID} value={employee.ID}>
                                                                {`${employee.FirstName ?? ''} ${employee.MiddleInitial ?? ''} ${employee.LastName ?? ''}`.trim()}
                                                            </option>
                                                        ))}
                                                    </>
                                                )}
                                            </select>
                                        </div>

                                        <div className="col-md-12 mt-3">
                                            <button className="btn btn-primary" onClick={handleFilterButtonClick}>
                                                Apply Filter
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {showOvertime && (
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="headingOvertime">
                                    <button
                                        className="accordion-button collapsed"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseOvertime"
                                        aria-expanded="false"
                                        aria-controls="collapseOvertime"
                                    >
                                        Overtime
                                    </button>
                                </h2>
                                <div
                                    id="collapseOvertime"
                                    className="accordion-collapse collapse"
                                    aria-labelledby="headingOvertime"
                                    data-bs-parent="#accordionOvertime"
                                >
                                    <div className="accordion-body">
                                        <table className="custom-table">
                                            <thead>
                                                <tr>
                                                    <th className="border-end-0">Transaction ID</th>
                                                    <th className="border-start-0 border-end-0">Transaction Date</th>
                                                    <th className="border-start-0 border-end-0">Employee</th>
                                                    <th className="border-start-0 border-end-0">Date Start</th>
                                                    <th className="border-start-0 border-end-0">Date End</th>
                                                    <th className="border-start-0 border-end-0">Duration</th>
                                                    <th className="border-start-0">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isOvertimeLoading ? (
                                                    <tr>
                                                        <td colSpan="8">
                                                            <div className="d-flex align-items-center">
                                                                <div className="spinner-border me-2" aria-hidden="true"></div>
                                                                <span role="status"> Loading...</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    overtimeData && overtimeData.length > 0 ?
                                                        overtimeData.map((item) => (
                                                            <React.Fragment key={item.ID}>
                                                                <tr>
                                                                    <td className="border-end-0" style={{ borderBottom: 'none' }}>{item.TransactionID}</td>
                                                                    <td className="border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.TransactionDate}</td>
                                                                    <td className="border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.Employee.Name}</td>
                                                                    <td className="border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.DateStart}</td>
                                                                    <td className="border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.DateEnd}</td>
                                                                    <td className="text-end border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.Duration}</td>
                                                                    <td className="border-start-0" style={{ borderBottom: 'none' }}>{item.Status}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td colSpan="8" style={{ borderTop: 'none', maxWidth: '50px' }}>
                                                                        <strong>Remarks:</strong> {item.Remarks}
                                                                    </td>
                                                                </tr>
                                                            </React.Fragment>
                                                        ))
                                                        : (
                                                            <tr>
                                                                <td colSpan="8">No Record</td>
                                                            </tr>
                                                        )
                                                )}
                                            </tbody>
                                        </table>
                                        <div className="transaction-pagination">
                                            {renderPagination(overtimeCurrentPage, overtimeTotalPages, setOvertimeCurrentPage, 'Overtime page navigation')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {showLeave && (
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="headingLeave">
                                    <button
                                        className="accordion-button collapsed"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseLeave"
                                        aria-expanded="false"
                                        aria-controls="collapseLeave"
                                    >
                                        Leave
                                    </button>
                                </h2>
                                <div
                                    id="collapseLeave"
                                    className="accordion-collapse collapse"
                                    aria-labelledby="headingLeave"
                                    data-bs-parent="#accordionLeave"
                                >
                                    <div className="accordion-body">
                                        <table className="custom-table">
                                            <thead>
                                                <tr>
                                                    <th className="border-end-0">Transaction ID</th>
                                                    <th className="border-start-0 border-end-0">Transaction Date</th>
                                                    <th className="border-start-0 border-end-0">Employee</th>
                                                    <th className="border-start-0 border-end-0">Leave Type</th>
                                                    <th className="border-start-0 border-end-0">Date Start</th>
                                                    <th className="border-start-0 border-end-0">Date End</th>
                                                    <th className="border-start-0 border-end-0">Duration</th>
                                                    <th className="border-start-0">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isLeaveLoading ? (
                                                    <tr>
                                                        <td colSpan="8">
                                                            <div className="d-flex align-items-center">
                                                                <div className="spinner-border me-2" aria-hidden="true"></div>
                                                                <span role="status"> Loading...</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    leavetimeData && leavetimeData.length > 0 ?
                                                        leavetimeData.map((item) => (
                                                            <React.Fragment key={item.ID}>
                                                                <tr>
                                                                    <td className="border-end-0" style={{ borderBottom: 'none' }}>{item.TransactionID}</td>
                                                                    <td className="border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.TransactionDate}</td>
                                                                    <td className="border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.Employee.Name}</td>
                                                                    <td className="border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.LeaveType}</td>
                                                                    <td className="border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.DateStart}</td>
                                                                    <td className="border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.DateEnd}</td>
                                                                    <td className="text-end border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.Duration}</td>
                                                                    <td className="border-start-0" style={{ borderBottom: 'none' }}>{item.Status}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td colSpan="8" style={{ borderTop: 'none', maxWidth: '50px' }}>
                                                                        <strong>Remarks:</strong> {item.Remarks}
                                                                    </td>
                                                                </tr>
                                                            </React.Fragment>
                                                        ))
                                                        : (
                                                            <tr>
                                                                <td colSpan="8">No Record</td>
                                                            </tr>
                                                        )
                                                )}
                                            </tbody>
                                        </table>
                                        <div className="transaction-pagination">
                                            {renderPagination(leaveCurrentPage, leaveTotalPages, setLeaveCurrentPage, 'Leave page navigation')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {showUndertime && (
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="headingUndertime">
                                    <button
                                        className="accordion-button collapsed"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseUndertime"
                                        aria-expanded="false"
                                        aria-controls="collapseUndertime"
                                    >
                                        Undertime
                                    </button>
                                </h2>
                                <div
                                    id="collapseUndertime"
                                    className="accordion-collapse collapse"
                                    aria-labelledby="headingUndertime"
                                    data-bs-parent="#accordionUndertime"
                                >
                                    <div className="accordion-body">
                                        <table className="custom-table">
                                            <thead>
                                                <tr>
                                                    <th className="border-end-0">Transaction ID</th>
                                                    <th className="border-start-0 border-end-0">Transaction Date</th>
                                                    <th className="border-start-0 border-end-0">Employee</th>
                                                    <th className="border-start-0 border-end-0">Date Start</th>
                                                    <th className="border-start-0 border-end-0">Date End</th>
                                                    <th className="border-start-0 border-end-0">Duration</th>
                                                    <th className="border-start-0">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isUndertimeLoading ? (
                                                    <tr>
                                                        <td colSpan="8">
                                                            <div className="d-flex align-items-center">
                                                                <div className="spinner-border me-2" aria-hidden="true"></div>
                                                                <span role="status"> Loading...</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    undertimeData && undertimeData.length > 0 ?
                                                        undertimeData.map((item) => (
                                                            <React.Fragment key={item.ID}>
                                                                <tr>
                                                                    <td className="border-end-0" style={{ borderBottom: 'none' }}>{item.TransactionID}</td>
                                                                    <td className="border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.TransactionDate}</td>
                                                                    <td className="border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.Employee.Name}</td>
                                                                    <td className="border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.DateStart}</td>
                                                                    <td className="border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.DateEnd}</td>
                                                                    <td className="text-end border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.Duration}</td>
                                                                    <td className="border-start-0" style={{ borderBottom: 'none' }}>{item.Status}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td colSpan="8" style={{ borderTop: 'none', maxWidth: '50px' }}>
                                                                        <strong>Remarks:</strong> {item.Remarks}
                                                                    </td>
                                                                </tr>
                                                            </React.Fragment>
                                                        ))
                                                        : (
                                                            <tr>
                                                                <td colSpan="8">No Record</td>
                                                            </tr>
                                                        )
                                                )}
                                            </tbody>
                                        </table>
                                        <div className="transaction-pagination">
                                            {renderPagination(undertimeCurrentPage, undertimeTotalPages, setUndertimeCurrentPage, 'Undertime page navigation')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {showTimeEntry && (
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="headingTimeEntry">
                                    <button
                                        className="accordion-button collapsed"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseTimeEntry"
                                        aria-expanded="false"
                                        aria-controls="collapseTimeEntry"
                                    >
                                        Time Entries
                                    </button>
                                </h2>
                                <div
                                    id="collapseTimeEntry"
                                    className="accordion-collapse collapse"
                                    aria-labelledby="headingTimeEntry"
                                    data-bs-parent="#accordionTimeEntry"
                                >
                                    <div className="accordion-body">
                                        <table className="custom-table">
                                            <thead>
                                                <tr>
                                                    <th className="border-end-0">Transaction Date</th>
                                                    <th className="border-start-0 border-end-0">Employee</th>
                                                    <th className="border-start-0 border-end-0">Start Date</th>
                                                    <th className="border-start-0 border-end-0">End Date</th>
                                                    <th className="border-start-0 border-end-0">Duration</th>
                                                    <th className="border-start-0 border-end-0">Entry Type</th>
                                                    <th className="border-start-0">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isTimeEntryLoading ? (
                                                    <tr>
                                                        <td colSpan="8">
                                                            <div className="d-flex align-items-center">
                                                                <div className="spinner-border me-2" aria-hidden="true"></div>
                                                                <span role="status"> Loading...</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    timeEntryData && timeEntryData.length > 0 ?
                                                        timeEntryData.map((item) => (
                                                            <React.Fragment key={item.ID}>
                                                                <tr>
                                                                    <td className="border-end-0" style={{ borderBottom: 'none' }}>{item.TransactionDate}</td>
                                                                    <td className="border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.Employee.Name}</td>
                                                                    <td className="border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.StartDate}</td>
                                                                    <td className="border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.EndDate}</td>
                                                                    <td className="text-end border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.Duration}</td>
                                                                    <td className="border-start-0 border-end-0" style={{ borderBottom: 'none' }}>{item.EntryTypeID}</td>
                                                                    <td className="border-start-0" style={{ borderBottom: 'none' }}>{item.Status}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td colSpan="8" style={{ borderTop: 'none', maxWidth: '50px' }}>
                                                                        <strong>Remarks:</strong> {item.Remarks}
                                                                    </td>
                                                                </tr>
                                                            </React.Fragment>
                                                        ))
                                                        : (
                                                            <tr>
                                                                <td colSpan="8">No Record</td>
                                                            </tr>
                                                        )
                                                )}
                                            </tbody>
                                        </table>
                                        <div className="transaction-pagination">
                                            {renderPagination(timeEntryCurrentPage, timeEntryTotalPages, setTimeEntryCurrentPage, 'Time Entry page navigation')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <div>
                    <h3>No access!</h3>
                    <button className="btn btn-primary" onClick={() => navigate(-1)}>
                        Go Back
                    </button>
                </div>
            )}
        </div>
    );
};

export default RVTransactions;
